import * as Yup from "yup";
import React, { Fragment, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import {
  Box,
  InputAdornment,
  IconButton,
  Stack,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link } from "react-router-dom";
import { useSnackbar } from "../../snackbar";
import Iconify from "../../iconify";
import { useBoolean } from "../../../hooks/use-boolean";
import { useResponsive } from "../../../hooks/use-responsive";



const emailOrUsernameSchema = Yup.string()
  .required("Username or Email is required")
  .test(
    "is-valid-email-or-username",
    "Must be a valid email or username",
    (value) => {
      if (!value) return false;
      
      // Email Regex
      const emailRegex = /^[a-zA-Z0-9._+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      // Username Regex
      const usernameRegex = /^[a-zA-Z0-9]{5,50}$/;

      // Check if it matches the email pattern or the username pattern
      const isEmail = emailRegex.test(value);
      const isUsername = usernameRegex.test(value);

      return isEmail || isUsername;
    }
  )
  .test(
    "is-allowed-email-domain",
    "Email domain must be one of the following: gmail.com, yahoo.com, outlook.com, protonmail.com, hotmail.com, zoho.com, megaconnect.cloud",
    (value) => {
      if (!value) return false;

      // If it's an email, validate the domain
      if (value.includes("@")) {
        const domain = value.split("@")[1];
        return [
          "gmail.com",
          "yahoo.com",
          "outlook.com",
          "protonmail.com",
          "mail.com",
          "zoho.com",
          "megaconnect.cloud"
        ].includes(domain);
      }
      
      // If it's a username, no domain check is needed
      return true;
    }
  );

export default function UserEmailIntegration() {
  const SERVER_URI = process.env.REACT_APP_SERVER_URI;
  const AUTHENTIK_LOGIN_URL = `${SERVER_URI}/ssologin`;
  const LOGIN_URL = `${SERVER_URI}/signin`;
  const mdUp = useResponsive("up", "md");

  const passwordShow = useBoolean();

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [emailData, setEmailData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);


  const UpdateEmailSchema = Yup.object().shape({
    email: emailOrUsernameSchema,
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),

  });

  const defaultValues = {
    email: emailData?.email || "",
    password: emailData?.password || "",
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    trigger,
    setValue,
    setError,
  } = useForm({
    resolver: yupResolver(UpdateEmailSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleAuthentikButtonClick = () => {
    window.open(AUTHENTIK_LOGIN_URL, "_self");
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const isEmailValid = await trigger("email");
    const isPasswordValid = await trigger("password")
    if (!isEmailValid) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!isPasswordValid) {
      setErrorMessage("Please enter a valid password.");
      return;
    }
    const email = getValues("email");
    const password= getValues("password")
    try {
      const response = await axios.post(LOGIN_URL, { email, password}); 
      // console.log(response.data)     
      enqueueSnackbar(response.data.message, { variant: "success" });
      localStorage.setItem("accessToken", response.data.token);
      setTimeout(() => {
        window.location.href = "/home";
      }, 4000);
    } catch (error) {
        console.log(error)
        if (error.response) {
          if (error.response.status === 404) {
              enqueueSnackbar(error.response.data.warning, { variant: "warning" });
          } else if (error.response.status === 409) {
              enqueueSnackbar(error.response.data.error, { variant: "error" });
          } else if (error.response.data && error.response.data.error) {
              enqueueSnackbar(error.response.data.error, { variant: "error" });
          } else {
              enqueueSnackbar("An unexpected error occurred. Please try again.", { variant: "error" });
          }
      } else {
          enqueueSnackbar("An unexpected error occurred. Please try again.", { variant: "error" });
      }
  } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={4} sx={{ mt: 8, mb: 8 }}>
          <Box>
            <Stack direction="column">
              <Typography
                variant="h3"
                sx={{
                  color: "#1D53E7",
                  fontFamily: "Sofia Sans Extra Condensed",
                  fontWeight: 700,
                }}
              >
                LOG IN{" "}
              </Typography>

            </Stack>
          </Box>
          <Stack direction='column' spacing={2}>
            <Stack direction="column" spacing={1}>
              <Typography
                component="label"
                htmlFor="email_text"
                sx={{
                  fontWeight: 400,
                  fontFamily: "Roboto Flex",
                  fontSize: "16px",
                  color: "#1F1F1F",
                }}
              >
                Enter Username or Email Address
              </Typography>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="email_text"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                      },
                    }}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="password_text"
                sx={{
                  fontWeight: 400,
                  fontFamily: "Roboto Flex",
                  fontSize: "16px",
                  color: "#1F1F1F",
                }}
              >
                Password
              </Typography>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="password_text"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                      },
                    }}
                    type={passwordShow.value ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={passwordShow.onToggle}>
                            <Iconify
                              icon={
                                passwordShow.value
                                  ? "solar:eye-bold"
                                  : "solar:eye-closed-bold"
                              }
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
             {/* Forgot Password Link */}
              <Box sx={{ textAlign: "right" }}>
                <Link
                  to="/user_forgot_pass"
                  style={{
                    color: "#1E53E8",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontFamily: "Roboto Flex",
                    fontWeight: 400,
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
          </Stack>

          <Box>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              sx={{ textAlign: "center", alignItems: "center" }}
            >
              <Grid container md={4} xs={8} justifyContent='center'  >
                <LoadingButton
                  size="large"
                  fullWidth
                  sx={{
                    fontFamily: "Roboto Flex",
                    backgroundColor: "#C7F760",
                    color: "#1E53E8",
                    textTransform: "none",
                    fontWeight: 900,
                    "&:hover": {
                      backgroundColor: "#1E53E8",
                      color: "#C7F760",
                    },
                  }}
                  type="submit"
                  loading={loading}
                  variant="contained"
                >
                  Login
                </LoadingButton>
              </Grid>
              <Grid container md={6} xs={8} justifyContent='center'  >
                <LoadingButton
                onClick={handleAuthentikButtonClick}
                  size="large"
                  fullWidth
                  sx={{
                    fontFamily: "Roboto Flex",
                    backgroundColor: "#FD4B2D",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": {
                      backgroundColor: "#1E53E8",
                      color: "#FFFFFF",
                    },
                  }}
                  type="button"
                  variant="contained"
                >
                  <Iconify icon="cbi:authentik" width="1.5em" height="1.5em" />&nbsp; Sign In with Authentik
                </LoadingButton>
              </Grid>
            </Stack>
            {mdUp && <Typography
                sx={{
                  mt:3,
                  fontSize: 16,
                  fontFamily: "Roboto Flex",
                  fontWeight: "400",
                  wordWrap: "break-word",
                  textAlign: "justify",
                  alignItems: "flex-end",
                }}
              >
                Don’t have an account?{" "}
                <Link
                  style={{ color: "#1E53E8" }}
                  onClick={() => window.open("/user_signup", "_self")}
                >
                Sign Up
                </Link>
              </Typography>}
            {!mdUp && <Typography
                sx={{
                  mt:3,
                  fontSize: 16,
                  fontFamily: "Roboto Flex",
                  fontWeight: "400",
                  wordWrap: "break-word",
                  textAlign: "center",
                  alignItems: "center",
                }}
              >
                Don’t have an account?{" "}
                <Link
                  style={{ color: "#1E53E8" }}
                  onClick={() => window.open("/user_signup", "_self")}
                >
                Sign Up
                </Link>
              </Typography>}
          </Box>
        </Stack>
      </form>
    </Fragment>
  );
}
